/**
 * Derive column-anchored crow's-foot edges from ERD foreign keys.
 *
 * One DiagramEdge per `tableContent.foreignKeys[]` entry, connecting the child
 * FK column row to the referenced parent PK column row. Handle ids come from
 * `columnHandleId` — the SAME helper DatabaseTableNode uses to render its
 * per-column handles — so a referenced handle can never be missing on the node.
 *
 * Cardinality is inferred from the FK column's flags, reading each crow's-foot
 * symbol AT its own entity:
 *   parent (one) end  <- FK nullability (it constrains whether a child MUST have
 *                        a parent): NOT NULL -> mandatory one ; nullable -> zero-or-one.
 *   child (many) end  <- FK uniqueness (nullability says nothing about how many
 *                        children a parent has): UNIQUE -> zero-or-one (1:1) ;
 *                        else -> zero-or-many. (DDL can't justify "one or more".)
 */

import type { DiagramNode, DiagramEdge } from '../types/schema'
import { columnHandleId } from './erdHandles'

type CrowFoot = 'crowsfoot-one' | 'crowsfoot-zero-or-one' | 'crowsfoot-zero-or-many'

function inferEnds(col?: { isUnique?: boolean; isNotNull?: boolean }): {
  childFoot: CrowFoot // arrowStart — at the child (FK / many) entity
  parentFoot: CrowFoot // arrowEnd — at the parent (PK / one) entity
  oneToOne: boolean
} {
  const unique = !!col?.isUnique
  const notNull = !!col?.isNotNull
  const childFoot: CrowFoot = unique ? 'crowsfoot-zero-or-one' : 'crowsfoot-zero-or-many'
  const parentFoot: CrowFoot = notNull ? 'crowsfoot-one' : 'crowsfoot-zero-or-one'
  return { childFoot, parentFoot, oneToOne: unique }
}

export function deriveForeignKeyEdges(
  nodes: DiagramNode[],
  existingEdges: DiagramEdge[] = []
): DiagramEdge[] {
  const have = new Set(
    existingEdges.map((e) => (e as any).data?.derivedFromFk).filter(Boolean) as string[]
  )
  const byTable = new Map<string, DiagramNode>()
  for (const n of nodes) {
    const name = n.tableContent?.tableName
    if (name) byTable.set(name, n)
  }

  const out: DiagramEdge[] = []
  for (const child of nodes) {
    const fks = child.tableContent?.foreignKeys
    if (!fks?.length) continue
    for (const fk of fks) {
      const childTable = child.tableContent!.tableName
      const key = `${childTable}.${fk.column}`
      if (have.has(key)) continue

      const parent = byTable.get(fk.references.table)
      const childCol = child.tableContent!.columns.find((c) => c.name === fk.column)
      const { childFoot, parentFoot, oneToOne } = inferEnds(childCol)

      const edge: DiagramEdge = {
        id: `fk-${child.id}-${fk.column}`,
        source: child.id,
        target: parent?.id ?? fk.references.table,
        edgeType: oneToOne ? 'one-to-one' : 'one-to-many',
        type: 'custom',
        data: {
          derivedFromFk: key,
          arrowStart: childFoot, // child (FK / many) end
          arrowEnd: parentFoot, // parent (PK / one) end
          pathStyle: 'smoothstep',
          color: '#94a3b8',
        },
      } as any

      if (!parent) {
        out.push(edge) // graceful table-level fallback (no dangling handle)
        continue
      }

      const childRightOfParent = (child.position?.x ?? 0) >= (parent.position?.x ?? 0)
      const childSide = childRightOfParent ? 'left' : 'right'
      const parentSide = childRightOfParent ? 'right' : 'left'
      ;(edge as any).sourceHandle = columnHandleId(fk.column, childSide)
      ;(edge as any).targetHandle = columnHandleId(fk.references.column, parentSide)
      ;(edge as any).data.fixedConnection = true
      out.push(edge)
    }
  }
  return out
}
